import React, {useEffect, useRef, useState} from 'react';
import Avatar from 'react-avatar';
import {
  ArrowRightOnRectangleIcon,
  ArrowUpCircleIcon,
  ChevronUpDownIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react-v2/24/outline';

import Link from '../Link';
import SearchModal from '../Search/SearchModal';
import Brand from '../Brand';
import {StarIcon} from '../svg';
import getActionLink from './actionLink';
import SidebarNav, {resolveLink} from './SidebarNav';
import SidebarPanel from './SidebarPanel';
import CollapseToggle from './CollapseToggle';
import ScrollFade from './ScrollFade';
import {useSidebar} from './SidebarContext';
import {
  BRAND_WORDMARK_INSET_CLASS,
  EASING_CLASS,
  GUTTER_CLASS,
  LABEL_INSET_CLASS,
  NEUTRAL_PLATE_CLASS,
  PRESS_PLATE_CLASS,
  RAIL_WIDTH_CLASS,
  SIDEBAR_WIDTH_CLASS,
  SLOT_CLASS,
  labelTransition,
} from './geometry';
import SidebarMenu from '../../constants/sidebarMenu';
import {useAppContext, useAnalytics} from '../../hooks';
import {cx, formatLargeNum} from '../../util';

/* --- Utility rows ---------------------------------------------------------
   Search and account extras with the same slot/label geometry as nav items,
   so they collapse to icons in the rail. */

const utilityLabelClasses = (showLabels) => cx(
  'absolute right-2.5 mt-px flex items-center whitespace-nowrap',
  LABEL_INSET_CLASS,
  labelTransition(showLabels),
);

/** The search row under the brand, opening the shared search modal. */
const SidebarSearch = () => {
  const {showLabels, requestFlyout} = useSidebar();
  const [searchOpen, setSearchOpen] = useState(false);
  const buttonRef = useRef(null);

  // This row owns ⌘K on desktop. The visibility guard keeps it from firing
  // below lg, where the sidebar is hidden and the drawer takes over.
  useEffect(() => {
    const onKeyDown = (e) => {
      if (!(e.metaKey || e.ctrlKey) || e.key !== 'k') return;
      if (!buttonRef.current || buttonRef.current.offsetParent === null) return;
      e.preventDefault();
      setSearchOpen(true);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  return (
    // Gutter and gap sit 1-2px over the nav rows' — the bordered field needs
    // a touch more air than a plain row.
    <div className="shrink-0 px-[7px] pb-1" onMouseEnter={() => requestFlyout(null)}>
      {/* Reads as an input when expanded; in the rail the border goes
          transparent (not removed, so nothing shifts). */}
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setSearchOpen(true)}
        className={cx(
          `group relative flex h-10 w-full items-center rounded-lg border text-sm font-medium
           text-gray-500 outline-none transition-colors duration-150
           hover:text-gray-700 focus-visible:ring-2 focus-visible:ring-indigo-400`,
          showLabels
            ? 'border-gray-200 bg-white hover:bg-gray-100'
            : 'border-transparent bg-transparent hover:bg-gray-100/80',
        )}
        aria-label="Search"
      >
        {/* -ml-px cancels the wider gutter so the icon stays on the same
            vertical line as the nav icons below it. */}
        <span className={cx(SLOT_CLASS, '-ml-px')}>
          <MagnifyingGlassIcon
            className="h-5 w-5 text-gray-400 transition-colors duration-150 group-hover:text-gray-600"
            aria-hidden="true"
          />
        </span>
        <span className={utilityLabelClasses(showLabels)}>
          Search
          <span className="ml-auto rounded border border-gray-200 px-1 py-px text-[11px] text-gray-400">
            ⌘K
          </span>
        </span>
      </button>
      <SearchModal isOpen={searchOpen} onClose={() => setSearchOpen(false)} />
    </div>
  );
};

/**
 * The account row pinned to the bottom of the sidebar, sharing the nav rows'
 * slot/label geometry. Its menu opens above the row on local state — NOT the
 * shared flyout machinery, which would slide the submenu panel open too.
 */
const SidebarAccount = ({onNavigate, upgradeLink}) => {
  const appContext = useAppContext();
  const {tracker} = useAnalytics();
  const {currentUser, hasSubscription, hasExpertSubscription} = appContext;
  const {showLabels, requestFlyout} = useSidebar();
  const [menuOpen, setMenuOpen] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const onDocumentClick = ({target}) => {
      if (containerRef.current?.contains(target)) return;
      setMenuOpen(false);
    };
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setMenuOpen(false);
    };
    document.addEventListener('mousedown', onDocumentClick);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onDocumentClick);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [menuOpen]);

  // Keep the footer's height while AppContext resolves the user, so the nav
  // above it doesn't shift once the real row appears.
  if (!currentUser) {
    return (
      <div className={cx('border-t border-gray-200 py-2', GUTTER_CLASS)} aria-hidden="true">
        <div className="relative flex h-12 items-center">
          <span className={SLOT_CLASS}>
            <span className="h-8 w-8 animate-pulse rounded-full bg-gray-200" />
          </span>
          <span className={cx('absolute right-3 space-y-1.5', LABEL_INSET_CLASS, labelTransition(showLabels))}>
            <span className="block h-2.5 w-24 animate-pulse rounded bg-gray-200" />
            <span className="block h-2 w-14 animate-pulse rounded bg-gray-100" />
          </span>
        </div>
      </div>
    );
  }

  const accountItems = SidebarMenu.accountItems.filter((item) => !item.shouldHide?.(appContext));
  // Karma gets a prominent slot in the menu header, and log out its own
  // segment — so neither renders as an ordinary middle item.
  const menuItems = accountItems.filter(({title}) => title !== 'Karma' && title !== 'Log out');
  const logoutItem = accountItems.find(({title}) => title === 'Log out');

  const clickItem = (item) => {
    setMenuOpen(false);
    onNavigate?.(item);
  };

  let membershipLabel = 'Basic';
  if (hasExpertSubscription) membershipLabel = 'Expert Member';
  else if (hasSubscription) membershipLabel = 'Member';

  return (
    <div
      ref={containerRef}
      onMouseEnter={() => requestFlyout(null)}
      className={cx('relative z-10 border-t border-gray-200 bg-white pt-[9px] pb-2', GUTTER_CLASS)}
    >
      {/* Conversion CTA: a solid button when expanded, a bare indigo icon in
          the rail (the gap collapses with the label to keep it centred). */}
      {getActionLink(appContext, upgradeLink)?.isUpgrade && (
        <Link
          href={upgradeLink}
          onClick={() => tracker.headerClickUpgrade({source: 'sidebar'})}
          className={cx(
            'mb-2 flex h-9 items-center justify-center rounded-lg text-sm font-semibold whitespace-nowrap transition-colors',
            showLabels
              ? 'mx-px gap-1.5 bg-indigo text-white hover:bg-indigo-900'
              : 'gap-0 text-indigo hover:bg-indigo-50',
          )}
        >
          <ArrowUpCircleIcon className="h-5 w-5 shrink-0" aria-hidden="true" />
          <span className={cx('overflow-hidden', labelTransition(showLabels), !showLabels && 'max-w-0')}>
            Upgrade
          </span>
        </Link>
      )}

      <div className="relative">
        {/* Anchored to this row so it opens above the profile button, not the
            upgrade CTA. No overflow-hidden: the tooltip pokes above it. */}
        <div
          className={cx(
            `absolute bottom-full left-0 z-40 mb-1 w-60 origin-bottom-left rounded-xl
             border border-gray-200 bg-white shadow-xl shadow-gray-900/10 transition duration-150 ease-out`,
            menuOpen
              ? 'visible scale-100 opacity-100'
              : 'pointer-events-none invisible scale-95 opacity-0',
          )}
        >
          <div className="flex items-center justify-between gap-2 border-b border-gray-100 px-4 py-3">
            <div className="min-w-0">
              <div className="truncate text-sm font-medium text-gray-900">
                {currentUser.displayName}
              </div>
              <div className="truncate text-xs text-gray-500">{currentUser.email}</div>
            </div>
            <Link
              href="/karma?src=nav"
              onClick={() => {
                setMenuOpen(false);
                tracker.karmaClickScore({source: 'sidebar-account'});
              }}
              className="group/karma relative flex shrink-0 cursor-pointer items-center gap-1 rounded-lg border border-gray-200 bg-white px-2 py-1 text-xs font-semibold text-gray-700 transition-colors hover:bg-gray-50"
            >
              <StarIcon className="h-3.5 w-3.5 text-yellow-400" aria-hidden="true" />
              {formatLargeNum(currentUser.karma)}
              <span
                role="tooltip"
                className="pointer-events-none absolute bottom-full left-1/2 z-40 mb-2 hidden -translate-x-1/2 whitespace-nowrap rounded-md bg-gray-900 px-2.5 py-1.5 text-xs font-medium text-white shadow-lg group-hover/karma:block motion-safe:animate-in motion-safe:fade-in motion-safe:duration-100"
              >
                Your karma
              </span>
            </Link>
          </div>
          <ul className="p-1.5">
            {menuItems.map((item) => (
              <li key={item.title}>
                <Link
                  href={resolveLink(item.link, appContext)}
                  forceAnchor={item.forceAnchor}
                  onClick={() => clickItem(item)}
                  className="group flex items-center gap-2 rounded-lg px-[9px] py-1.5 text-[13px] font-medium text-gray-700 transition-colors hover:bg-gray-50 hover:text-indigo"
                >
                  {item.icon && (
                    <item.icon
                      className="h-[18px] w-[18px] shrink-0 text-gray-400 transition-colors group-hover:text-indigo"
                      aria-hidden="true"
                    />
                  )}
                  {item.title}
                </Link>
              </li>
            ))}
          </ul>
          {logoutItem && (
            <div className="border-t border-gray-100 p-1.5">
              <Link
                href={logoutItem.link}
                forceAnchor={logoutItem.forceAnchor}
                onClick={() => clickItem(logoutItem)}
                className="group flex items-center gap-2 rounded-lg px-[9px] py-1.5 text-[13px] font-medium text-gray-700 transition-colors hover:bg-gray-50 hover:text-gray-900"
              >
                <ArrowRightOnRectangleIcon
                  className="h-[18px] w-[18px] shrink-0 text-gray-400 transition-colors group-hover:text-gray-600"
                  aria-hidden="true"
                />
                {logoutItem.title}
              </Link>
            </div>
          )}
        </div>

        <button
          type="button"
          aria-haspopup="true"
          aria-expanded={menuOpen}
          aria-label={`Account menu for ${currentUser.displayName}`}
          onClick={() => setMenuOpen((prev) => !prev)}
          className="group relative flex h-12 w-full items-center rounded-lg outline-none focus-visible:ring-2 focus-visible:ring-indigo-400"
        >
          <span
            aria-hidden="true"
            className={cx(
              PRESS_PLATE_CLASS,
              NEUTRAL_PLATE_CLASS,
              menuOpen && 'bg-gray-100/80',
            )}
          />
          <span className={cx(SLOT_CLASS, 'relative')}>
            <Avatar
              name={currentUser.displayName}
              src={currentUser.profile_img || ''}
              color="#EEEBFB"
              fgColor="#5524e0"
              size={32}
              round
            />
          </span>
          <span
            className={cx(
              'absolute right-2 flex items-center gap-1',
              LABEL_INSET_CLASS,
              labelTransition(showLabels),
            )}
          >
            {/* pr-14 keeps long names from running under the karma pill, which
                overlays this row as a sibling of the button. */}
            <span className="min-w-0 pr-14 text-left">
              <span className="block truncate text-sm font-medium text-gray-900">
                {currentUser.displayName}
              </span>
              <span className={cx('block truncate text-xs', hasSubscription ? 'text-indigo' : 'text-gray-400')}>
                {membershipLabel}
              </span>
            </span>
            <ChevronUpDownIcon className="ml-auto h-4 w-4 shrink-0 text-gray-400" aria-hidden="true" />
          </span>
        </button>

        {/* Overlaid as a sibling of the row button (not a child) so it can be
            a real link; the rest of the row still opens the menu. */}
        <Link
          href="/karma?src=nav"
          onClick={() => tracker.karmaClickScore({source: 'sidebar-account'})}
          className={cx(
            `group/karma absolute top-1/2 right-8 z-10 flex -translate-y-1/2 items-center gap-1
             rounded-lg border border-gray-200 bg-white px-2 py-1 text-xs font-semibold text-gray-700
             transition-colors hover:bg-gray-50`,
            labelTransition(showLabels),
          )}
        >
          <StarIcon className="h-3.5 w-3.5 text-yellow-400" aria-hidden="true" />
          {formatLargeNum(currentUser.karma)}
          {/* Same look as the rail tooltips, anchored above since the row hugs
              the viewport bottom. */}
          <span
            role="tooltip"
            className="pointer-events-none absolute bottom-full left-1/2 z-40 mb-2 hidden -translate-x-1/2 whitespace-nowrap rounded-md bg-gray-900 px-2.5 py-1.5 text-xs font-medium text-white shadow-lg group-hover/karma:block motion-safe:animate-in motion-safe:fade-in motion-safe:duration-100"
          >
            Your karma
          </span>
        </Link>
      </div>
    </div>
  );
};

/**
 * The primary logged-in navigation: a full-height column that either shows
 * icons with labels, or collapses to an icon rail that expands over the page
 * content on hover.
 *
 * Rendered as a `sticky` sibling of the content column rather than `fixed`, so
 * the document keeps its normal scroll behaviour and page banners above the
 * shell are not covered. The outer element holds the layout width while the
 * inner panel is absolutely positioned, which is what lets a hover expansion
 * overlay the page instead of reflowing it.
 */
/**
 * @param {string} [props.upgradeLink] Destination for the account section's
 *   upgrade CTA.
 */
const Sidebar = ({upgradeLink}) => {
  const {tracker} = useAnalytics();
  const {
    collapsed,
    showLabels,
    hoverExpanded,
    flyout,
    handleHoverEnter,
    handleHoverLeave,
    handlePointerMove,
    requestFlyout,
  } = useSidebar();

  const appContext = useAppContext();

  const trackAccountClick = (item) => {
    tracker.headerClickMenu({platform: 'sidebar', title: item.title, link: resolveLink(item.link, appContext)});
  };

  // A page banner above the shell pushes the sidebar down, hiding the account
  // row below the fold — shave the visible offset off the sidebar's height.
  const asideRef = useRef(null);
  const [topOffset, setTopOffset] = useState(0);

  useEffect(() => {
    const measure = () => {
      if (!asideRef.current) return;
      const {top} = asideRef.current.getBoundingClientRect();
      // setState with an unchanged number is a no-op, so scrolling with no
      // banner (top stays 0) never re-renders.
      setTopOffset(Math.max(0, Math.round(top)));
    };
    measure();

    window.addEventListener('scroll', measure, {passive: true});
    window.addEventListener('resize', measure);
    // Catches banner dismissal, which changes layout without a scroll.
    const resizeObserver = new ResizeObserver(measure);
    resizeObserver.observe(document.body);
    return () => {
      window.removeEventListener('scroll', measure);
      window.removeEventListener('resize', measure);
      resizeObserver.disconnect();
    };
  }, []);

  return (
    <aside
      ref={asideRef}
      aria-label="Sidebar"
      onMouseEnter={handleHoverEnter}
      onMouseLeave={handleHoverLeave}
      onMouseMove={handlePointerMove}
      style={topOffset > 0 ? {height: `calc(100vh - ${topOffset}px)`} : undefined}
      className={cx(
        'sticky top-0 z-40 hidden h-screen shrink-0 transition-[width] duration-300 lg:block',
        EASING_CLASS,
        collapsed ? RAIL_WIDTH_CLASS : SIDEBAR_WIDTH_CLASS,
      )}
    >
      {/* The nav panel and the submenu panel share this positioning context.
          The submenu is a sibling pinned to the nav's right edge and painted
          beneath it, so it can slide out from behind an opaque sidebar. */}
      <div
        className={cx(
          'absolute inset-y-0 left-0 transition-[width] duration-300',
          EASING_CLASS,
          showLabels ? SIDEBAR_WIDTH_CLASS : RAIL_WIDTH_CLASS,
        )}
      >
        <SidebarPanel />

        <div
          className={cx(
            'relative z-10 flex h-full flex-col border-r border-gray-200 bg-white transition-shadow duration-300',
            (hoverExpanded || flyout) && 'shadow-[8px_0_24px_-12px_rgba(15,23,42,0.18)]',
          )}
        >
          {/* Brand. The mark sits in the same fixed slot as every nav icon, so
              it stays put while the panel resizes, and the wordmark is
              positioned absolutely at the label inset so it can only fade. */}
          <div
            className={cx('relative flex h-14 shrink-0 items-center', GUTTER_CLASS)}
            onMouseEnter={() => requestFlyout(null)}
          >
            <Brand
              className="w-full"
              markClassName="h-6 w-6"
              slotClassName={SLOT_CLASS}
              fkaBadge
              wordmarkClassName={cx(
                'absolute right-12 h-4',
                BRAND_WORDMARK_INSET_CLASS,
                labelTransition(showLabels),
              )}
            />
            <CollapseToggle className="absolute right-2.5" />
          </div>

          <ScrollFade className="pb-4">
            {/* Inside the scroll container on purpose: the search row scrolls
                away with the nav rather than staying pinned. */}
            <SidebarSearch />
            <SidebarNav />
          </ScrollFade>

          <SidebarAccount onNavigate={trackAccountClick} upgradeLink={upgradeLink} />
        </div>
      </div>
    </aside>
  );
};

export default Sidebar;
